import { useDialogFocus } from './useDialogFocus';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Calendar } from '../../shared/contracts';
import { api, ApiRequestError, type EventPlacement } from '../api';

export function EventPlacementDialog({
  eventId,
  calendars,
  csrfToken,
  onClose,
  onSaved,
}: {
  eventId: string;
  calendars: Calendar[];
  csrfToken?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const dialogRef = useDialogFocus(onClose);
  const [placement, setPlacement] = useState<EventPlacement>();
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [reviewNotice, setReviewNotice] = useState('');
  const baseline = useRef<string[]>([]);
  const latestLoad = useRef(0);

  const load = useCallback(
    async (preserveIntent = false) => {
      const loadId = ++latestLoad.current;
      setLoading(true);
      setError('');
      try {
        const current = await api.eventPlacements(eventId);
        if (loadId !== latestLoad.current) return;
        const previousBaseline = baseline.current;
        setPlacement(current);
        setSelected((pending) => {
          if (!preserveIntent) return current.calendarIds;
          const additions = pending.filter((id) => !previousBaseline.includes(id));
          const removals = previousBaseline.filter((id) => !pending.includes(id));
          return [...new Set([...current.calendarIds, ...additions])]
            .filter((id) => !removals.includes(id))
            .sort();
        });
        baseline.current = current.calendarIds;
        setReviewNotice(
          preserveIntent
            ? 'Latest placements loaded. Your pending additions and removals are still applied. Review before saving.'
            : '',
        );
        setConflict(false);
      } catch (cause) {
        if (loadId !== latestLoad.current) return;
        setError(cause instanceof Error ? cause.message : 'Calendar placement could not load');
      } finally {
        if (loadId === latestLoad.current) setLoading(false);
      }
    },
    [eventId],
  );
  useEffect(() => {
    void load();
    return () => {
      latestLoad.current++;
    };
  }, [load]);

  const owner = calendars.find((calendar) => calendar.id === placement?.ownerCalendarId);
  const available = useMemo(
    () =>
      calendars.filter(
        (calendar) =>
          calendar.id !== placement?.ownerCalendarId &&
          calendar.canEdit &&
          (owner?.visibility !== 'private' || calendar.visibility === 'private'),
      ),
    [calendars, owner, placement?.ownerCalendarId],
  );
  const lockedVisible = calendars.filter(
    (calendar) =>
      placement?.calendarIds.includes(calendar.id) &&
      !available.some((item) => item.id === calendar.id),
  );
  const hiddenCount =
    placement?.calendarIds.filter((id) => !calendars.some((calendar) => calendar.id === id))
      .length ?? 0;

  const save = async () => {
    if (!placement) return;
    setSaving(true);
    setError('');
    try {
      await api.updateEventPlacements(eventId, placement.version, selected, csrfToken);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Calendar placement could not be saved');
      setConflict(cause instanceof ApiRequestError && cause.status === 409);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        className="placement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="placement-title"
      >
        <button
          className="icon-button summary-close"
          aria-label="Close calendar placement"
          onClick={onClose}
        >
          ×
        </button>
        <span className="eyebrow">Event organization</span>
        <h2 id="placement-title">Calendar placement</h2>
        {loading && <p role="status">Loading calendars…</p>}
        {error && (
          <p className="inline-error" role="alert">
            {error}
          </p>
        )}
        {reviewNotice && <p role="status">{reviewNotice}</p>}
        {placement && !loading && (
          <>
            <p className="placement-owner">
              <b>Owned by {owner?.name ?? 'a calendar you can edit'}</b>
              <span>The owning calendar controls who may read and edit this event.</span>
            </p>
            {owner?.visibility === 'private' && (
              <p className="placement-note">
                Private events can only be shared with other private calendars.
              </p>
            )}
            <fieldset className="placement-options">
              <legend>Also show this event on</legend>
              {available.map((calendar) => (
                <label key={calendar.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(calendar.id)}
                    onChange={(event) =>
                      setSelected((current) =>
                        event.target.checked
                          ? [...current, calendar.id]
                          : current.filter((id) => id !== calendar.id),
                      )
                    }
                  />
                  <i style={{ background: calendar.color }} />
                  <span>
                    <b>{calendar.name}</b>
                    <small>
                      {calendar.visibility === 'private' ? 'Members only' : 'Public calendar'}
                    </small>
                  </span>
                </label>
              ))}
              {lockedVisible.map((calendar) => (
                <label key={calendar.id} className="placement-locked">
                  <input type="checkbox" checked disabled />
                  <i style={{ background: calendar.color }} />
                  <span>
                    <b>{calendar.name}</b>
                    <small>Kept because you cannot edit this placement</small>
                  </span>
                </label>
              ))}
              {hiddenCount > 0 && (
                <p className="placement-note">
                  {hiddenCount} existing {hiddenCount === 1 ? 'placement is' : 'placements are'}{' '}
                  hidden by your access. {hiddenCount === 1 ? 'It will' : 'They will'} be preserved.
                </p>
              )}
              {!available.length && !lockedVisible.length && !hiddenCount && (
                <p className="placement-note">No other calendars are available for this event.</p>
              )}
            </fieldset>
            <div className="summary-actions">
              <button className="button" disabled={saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save calendars'}
              </button>
              {conflict && (
                <button
                  className="button button-quiet"
                  disabled={loading}
                  onClick={() => void load(true)}
                >
                  Reload and review
                </button>
              )}
              <button className="button button-quiet" disabled={saving} onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
